import { track } from "@vercel/analytics";
import { analyticsAllowed } from "./analytics-privacy.js";

export type PartAcquisitionEventName =
  | "part_landing_impression"
  | "part_live_refresh"
  | "part_live_scan_activation"
  | "alternative_comparison"
  | "rfq";

export interface PartAcquisitionEvent {
  eventName: PartAcquisitionEventName;
  slug: string;
  manufacturer: string;
  normalizedMpn: string;
  surface: "part_landing";
  properties?: Record<string, string | number | boolean | null>;
}

const ANONYMOUS_ID_KEY = "control-exchange:part-acquisition-id:v1";

function anonymousId(): string {
  if (typeof window === "undefined") return "server";
  try {
    const current = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (current) return current;
    const created = crypto.randomUUID();
    localStorage.setItem(ANONYMOUS_ID_KEY, created);
    return created;
  } catch {
    return `ephemeral:${crypto.randomUUID()}`;
  }
}

/**
 * Record acquisition actions through a same-origin service endpoint. The
 * keepalive request survives link navigation, while Vercel Analytics provides
 * the aggregate acquisition view without becoming the source of record.
 */
export async function recordPartAcquisitionEvent(input: PartAcquisitionEvent): Promise<void> {
  if (typeof window === "undefined") return;
  const passiveImpression = input.eventName === "part_landing_impression";
  const properties = {
    slug: input.slug,
    manufacturer: input.manufacturer,
    normalized_mpn: input.normalizedMpn,
    surface: input.surface,
    ...(input.properties || {}),
  };
  if (analyticsAllowed()) {
    try {
      track(input.eventName, properties);
    } catch {
      // Persistence below remains the source of record when analytics is blocked.
    }
  }
  // Passive page views stay inside the optional, aggregate Vercel analytics
  // path. Do not create a stable first-party identifier or operational event
  // until the visitor performs an action that actually runs sourcing work.
  if (passiveImpression) return;
  try {
    await fetch("/api/part-acquisition-event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventName: input.eventName,
        anonymousId: anonymousId(),
        ...properties,
      }),
      keepalive: true,
    });
  } catch {
    // Analytics must never block a procurement action.
  }
}
